import {Action, Card, DataStatus} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {customerRoutes} from '@dms/routes';

import {buildArray, composePath, TestIdProps} from 'shared/web';

import {useEditCustomer} from '../../hooks/useEditCustomer';
import {CustomerInformationAttributes} from '../CustomerInformationAttributes/CustomerInformationAttributes';

interface CustomerInformationCardProps extends TestIdProps {
  customerId: string;
  isDetailLinkVisible?: boolean;
  onChange?: VoidFunction;
  isEditingDisabled?: boolean;
  isCustomerChangeDisabled?: boolean;
  isChangeCustomerForbidden?: boolean;
  resourceId?: string;
  recordId?: string;
}

export function CustomerInformationCard(props: CustomerInformationCardProps) {
  const {data, isLoading, isError} = useGetCustomerV2Query({customerId: props.customerId});

  const editCustomer = useEditCustomer(data);

  const actions = buildArray<Action>()
    .when(props.isDetailLinkVisible, {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.labels.detail'),
      leftIcon: 'action/open_in_new',
      isDisabled: isNil(data),
      to: composePath(customerRoutes.detail, {
        params: {id: props.customerId},
        isSearchParamsPreserved: false,
      }),
    })
    .when(isNotNil(props.onChange), {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.change'),
      leftIcon: 'navigation/refresh',
      isDisabled: isNil(data) || props.isCustomerChangeDisabled || props.isChangeCustomerForbidden,
      onClick: props.onChange,
    })
    .add({
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.edit'),
      leftIcon: 'editor/mode',
      isDisabled:
        isNil(data) ||
        props.isEditingDisabled ||
        props.isChangeCustomerForbidden ||
        props.isCustomerChangeDisabled,
      onClick: editCustomer,
      ...{'data-testid': props['data-testid']},
    });

  return (
    <Card
      title={i18n.t('entity.customer.labels.customerInformation')}
      actions={actions}
      data-testid={props['data-testid']}
    >
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        minHeight={90}
        data-testid={props['data-testid']}
      >
        <VStack spacing={2}>
          {data && (
            <CustomerInformationAttributes
              customer={data}
              resourceId={props.resourceId}
              recordId={props.recordId}
              data-testid={props['data-testid']}
            />
          )}
        </VStack>
      </DataStatus>
    </Card>
  );
}
